import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone, Globe } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { useBusinessInfo } from '../context/BusinessContext';

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const { language, toggleLanguage, t } = useLanguage();
  const { getWhatsAppLink } = useBusinessInfo();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { path: '/', label: t('nav.home') },
    { path: '/cabanas', label: t('nav.cabins') },
    { path: '/reservar', label: t('nav.book') },
    { path: '/mazamitla', label: t('nav.mazamitla') },
    { path: '/contacto', label: t('nav.contact') }
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <nav
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled || location.pathname !== '/'
          ? 'bg-white/95 backdrop-blur-md shadow-sm'
          : 'bg-transparent'
      }`}
    >
      <div className="container-ribba">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link
            to="/"
            data-testid="logo-link"
            className="flex items-center gap-2"
          >
            <span
              className={`font-heading text-2xl font-bold transition-colors ${
                isScrolled || location.pathname !== '/'
                  ? 'text-[#2F3E30]'
                  : 'text-white'
              }`}
            >
              Cabañas RIBBA
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`nav-link-${link.path.replace('/', '') || 'home'}`}
                className={`text-sm font-medium tracking-wide transition-colors relative group ${
                  isScrolled || location.pathname !== '/'
                    ? 'text-[#1A1A1A] hover:text-[#2F3E30]'
                    : 'text-white/90 hover:text-white'
                } ${isActive(link.path) ? 'font-semibold' : ''}`}
              >
                {link.label}
                <span
                  className={`absolute -bottom-1 left-0 h-0.5 bg-[#C6A87C] transition-all duration-300 ${
                    isActive(link.path) ? 'w-full' : 'w-0 group-hover:w-full'
                  }`}
                />
              </Link>
            ))}
          </div>

          {/* CTA Button and Language Selector */}
          <div className="hidden md:flex items-center gap-3">
            {/* Language Toggle */}
            <button
              onClick={toggleLanguage}
              data-testid="language-toggle"
              className={`flex items-center gap-1.5 px-3 py-2 rounded-sm transition-all font-medium text-sm ${
                isScrolled || location.pathname !== '/'
                  ? 'text-[#2F3E30] hover:bg-[#F5F2EB]'
                  : 'text-white hover:bg-white/20'
              }`}
              title={language === 'es' ? 'Switch to English' : 'Cambiar a Español'}
            >
              <Globe className="w-4 h-4" />
              <span>{language === 'es' ? 'EN' : 'ES'}</span>
            </button>

            <a
              href={getWhatsAppLink()}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="whatsapp-nav-btn"
              className={`flex items-center gap-2 px-4 py-2 rounded-sm transition-all ${
                isScrolled || location.pathname !== '/'
                  ? 'bg-[#2F3E30] text-white hover:bg-[#1F2B21]'
                  : 'bg-white text-[#2F3E30] hover:bg-[#C6A87C] hover:text-white'
              }`}
            >
              <Phone className="w-4 h-4" />
              <span className="text-sm font-medium">{t('nav.contact')}</span>
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            data-testid="mobile-menu-btn"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className={`md:hidden p-2 transition-colors ${
              isScrolled || location.pathname !== '/'
                ? 'text-[#2F3E30]'
                : 'text-white'
            }`}
          >
            {isMobileMenuOpen ? (
              <X className="w-6 h-6" />
            ) : (
              <Menu className="w-6 h-6" />
            )}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div
            data-testid="mobile-menu"
            className="md:hidden absolute top-20 left-0 right-0 bg-white shadow-lg animate-fade-in"
          >
            <div className="container-ribba py-6 flex flex-col gap-4">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  data-testid={`mobile-nav-link-${link.path.replace('/', '') || 'home'}`}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className={`text-base font-medium py-2 border-b border-[#E5E0D6] ${
                    isActive(link.path)
                      ? 'text-[#2F3E30] font-semibold'
                      : 'text-[#5A5A5A]'
                  }`}
                >
                  {link.label}
                </Link>
              ))}
              
              {/* Language toggle in mobile */}
              <button
                onClick={toggleLanguage}
                className="flex items-center gap-2 py-2 text-base font-medium text-[#5A5A5A] border-b border-[#E5E0D6]"
              >
                <Globe className="w-4 h-4" />
                <span>{language === 'es' ? 'English' : 'Español'}</span>
              </button>

              <a
                href={getWhatsAppLink()}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 mt-4 px-6 py-3 bg-[#2F3E30] text-white rounded-sm"
              >
                <Phone className="w-4 h-4" />
                <span>{language === 'es' ? 'Contactar por WhatsApp' : 'Contact via WhatsApp'}</span>
              </a>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
